import { Link } from "react-router-dom";
import { ArrowRight, Wrench, Zap, MapPin } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/site/Reveal";
import { RequestButton, WhatsAppButton } from "@/components/site/Buttons";
import { usePageMeta } from "@/lib/usePageMeta";
import { GALLERY } from "@/content/site";

const FACTS = [
  { icon: Wrench, k: "Background", v: "Qualified PSV mechanic" },
  { icon: Zap, k: "On the tools", v: "Heavy-duty Milwaukee, every day" },
  { icon: MapPin, k: "Started", v: "2026 · Newcastle upon Tyne" },
];

const Story = () => (
  <section className="py-20 md:py-28 border-b border-[var(--line)]" data-testid="about-story">
    <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-12">
      <Reveal className="lg:col-span-5">
        <div className="aspect-square overflow-hidden chamfer border border-[var(--line)]">
          <img src={GALLERY[1]?.src} alt={GALLERY[1]?.alt} loading="lazy" className="h-full w-full object-cover" />
        </div>
        <p className="mt-3 font-mono text-xs uppercase tracking-[0.18em] text-[var(--mute)]">Where it started: a run-over impact gun</p>
      </Reveal>
      <Reveal className="lg:col-span-7 lg:pl-8" delay={0.08}>
        <Eyebrow n="01">How it started</Eyebrow>
        <h2 className="mt-5 font-display font-black uppercase text-5xl md:text-7xl leading-[0.9]">One broken<br />impact gun</h2>
        <div className="mt-8 grid gap-5 text-base md:text-lg text-[var(--grey)] leading-relaxed max-w-2xl">
          <p>I'm Harrison, a qualified PSV mechanic. I use heavy-duty Milwaukee tools every day at work, so I know how much you depend on them and how frustrating it is when one stops working.</p>
          <p>When my own Milwaukee impact gun got run over, I repaired it myself rather than writing it off. That job is what started NE Tool Repair.</p>
          <p>Since 2026 I've been repairing and refurbishing power tools for tool owners in Newcastle and across the North East, with a focus on Milwaukee M12 and M18.</p>
        </div>
        <p className="mt-8 font-display font-extrabold uppercase tracking-[0.14em] text-white">— Harrison, NE Tool Repair</p>
      </Reveal>
    </div>
  </section>
);

export default function About() {
  usePageMeta(
    "About NE Tool Repair | Independent Power Tool Repair Newcastle",
    "NE Tool Repair is run by Harrison, a qualified PSV mechanic repairing Milwaukee M12 & M18 power tools in Newcastle upon Tyne and the North East.",
    "/about"
  );
  return (
    <div data-testid="about-page">
      <section className="relative border-b border-[var(--line)] overflow-hidden">
        <div className="absolute inset-0 grid-lines [mask-image:linear-gradient(to_bottom,black,transparent)]" aria-hidden />
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 pt-16 md:pt-24 pb-14 md:pb-20">
          <Reveal>
            <Eyebrow>About NE Tool Repair</Eyebrow>
            <h1 className="mt-6 font-display font-black uppercase text-6xl sm:text-7xl lg:text-[7.5rem] leading-[0.86]">Independent.<br /><span className="text-[var(--red)]">North East.</span><br /><span className="stroke-text">Hands-on.</span></h1>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-3 gap-4" data-testid="about-facts">
            {FACTS.map((f, i) => (
              <Reveal key={f.k} delay={i * 0.06}>
                <div className="metal metal-edge chamfer-sm border border-[var(--line)] p-6 h-full">
                  <f.icon className="h-5 w-5 text-[var(--red)]" aria-hidden />
                  <p className="mt-6 font-mono text-xs uppercase tracking-[0.18em] text-[var(--mute)]">{f.k}</p>
                  <p className="mt-2 font-display font-extrabold uppercase text-2xl md:text-3xl leading-tight">{f.v}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <Story />
      <section className="py-20 md:py-28 border-b border-[var(--line)] bg-[var(--char)]" data-testid="about-cta">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-end">
          <Reveal className="lg:col-span-7">
            <Eyebrow n="02">See the work</Eyebrow>
            <h2 className="mt-5 font-display font-black uppercase text-5xl md:text-7xl leading-[0.9]">Got a tool<br />that needs work?</h2>
            <Link to="/repairs-services" className="group mt-8 inline-flex items-center gap-3 font-display font-extrabold uppercase tracking-[0.14em] text-sm border-b-2 border-[var(--red)] pb-1" data-testid="about-services-link">
              Repairs &amp; services <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden />
            </Link>
          </Reveal>
          <Reveal className="lg:col-span-5 flex flex-col sm:flex-row lg:justify-end gap-3" delay={0.1}>
            <RequestButton testId="about-request-btn" />
            <WhatsAppButton testId="about-whatsapp-btn" />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
